import React, { useState } from "react";
import { Target, Clock, CheckCircle2 } from "lucide-react";
import MagicalCard from "./MagicalCard";
import { Icon, EmptyState } from "./common";
import { useLang } from "../i18n";
import api, { apiError } from "../api";
import { toast } from "sonner";

export function ChallengeStatusChip({ status }) {
  const { t } = useLang();
  const styles = {
    not_started: "bg-[#A5A0D6]/15 text-[#A5A0D6] border-[#A5A0D6]/30",
    active: "bg-[#48D276]/15 text-[#48D276] border-[#48D276]/40",
    completed: "bg-[#FFC83D]/15 text-[#FFC83D] border-[#FFC83D]/40",
    ended: "bg-white/8 text-[#A5A0D6] border-white/15",
    awaiting: "bg-[#27C9EE]/15 text-[#27C9EE] border-[#27C9EE]/40",
    rejected: "bg-[#EF4D9A]/15 text-[#FF8FB1] border-[#EF4D9A]/40",
  };
  return (
    <span data-testid={`challenge-status-${status}`} className={`text-[11px] font-extrabold px-2.5 py-1 rounded-full border ${styles[status] || styles.active}`}>
      {t(`challenges.${status}`)}
    </span>
  );
}

export function daysLeftText(ch, t) {
  if (ch.days_remaining == null) return null;
  if (ch.days_remaining <= 0) return t("challenges.ends_today");
  if (ch.days_remaining === 1) return t("challenges.day_left");
  return t("challenges.days_left", { n: ch.days_remaining });
}

export default function WeeklyChallengeCard({ challenge, onCompleted, isStudent }) {
  const { t, pick } = useLang();
  const [busy, setBusy] = useState(false);
  if (!challenge) {
    return (
      <MagicalCard title={t("dash.weekly_challenge")} variant="ribbon-gold" testId="weekly-challenge-card">
        <EmptyState icon="Target" title={t("challenges.empty")} testId="challenge-empty" />
      </MagicalCard>
    );
  }
  const complete = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/challenges/${challenge.id}/complete`);
      toast.success(data.status === "awaiting" ? t("challenges.submitted") : t("challenges.completed_toast"));
      onCompleted?.(data);
    } catch (e) {
      toast.error(apiError(e, t));
    } finally { setBusy(false); }
  };
  return (
    <MagicalCard title={t("dash.weekly_challenge")} variant="ribbon-gold" testId="weekly-challenge-card">
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#EF4D9A] to-[#FF63B5] flex items-center justify-center shrink-0 glow-pink animate-floaty">
          <Icon name={challenge.icon || "Target"} className="w-7 h-7 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-black text-lg">{pick(challenge, "title")}</h4>
            <ChallengeStatusChip status={challenge.status} />
          </div>
          <p className="text-sm text-[#D8D4F2] font-semibold mt-1">{pick(challenge, "desc")}</p>
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            <span className="text-sm font-black text-[#FFC83D] flex items-center gap-1">
              <Target className="w-4 h-4" /> {t("challenges.reward")}: +{challenge.points}
            </span>
            {daysLeftText(challenge, t) && (
              <span className="text-xs font-bold text-[#27C9EE] flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" /> {daysLeftText(challenge, t)}
              </span>
            )}
          </div>
          {isStudent && challenge.status === "active" && (
            <button data-testid="complete-challenge-btn" disabled={busy} onClick={complete}
              className="btn-magic btn-gold mt-4 text-sm">
              <CheckCircle2 className="w-4 h-4" /> {t("challenges.complete")}
            </button>
          )}
        </div>
      </div>
    </MagicalCard>
  );
}
